import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { API_URLS } from '../../config/api';
import { Briefcase, ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';

const iconMap = {
  Briefcase
};

const ProjectsCatalogPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contentReady, setContentReady] = useState(false);

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await fetch(API_URLS.PROJECT_CATEGORIES);
        const data = await res.json();
        if (Array.isArray(data)) {
          // Decode JSON fields if backend didn't
          const normalized = data.map(cat => ({
            ...cat,
            subcategories: Array.isArray(cat.subcategories) ? cat.subcategories : (cat.subcategories ? JSON.parse(cat.subcategories) : []),
            features: Array.isArray(cat.features) ? cat.features : (cat.features ? JSON.parse(cat.features) : [])
          }));
          setCategories(normalized);
        } else {
          setCategories([]);
        }
      } catch (e) {
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50">
      <section 
        className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-16"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-6">
            <Link to="/projeler" className="flex items-center gap-2 text-white/80 hover:text-white">
              <ArrowLeft className="w-5 h-5" /> Geri
            </Link>
          </div>
          <div className="text-center space-y-4">
            <div className="inline-flex items-center space-x-3 bg-white/20 px-6 py-3 rounded-full">
              <Briefcase className="w-5 h-5" />
              <span>Tüm Proje Kategorileri</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold">Kategoriler ve Alt Kategoriler</h1>
            <p className="text-purple-100 max-w-2xl mx-auto">İhtiyacınıza en uygun kategori ve alt kategoriye giderek örnek projeleri inceleyin.</p>
          </div>
        </div>
      </section>

      <div 
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {categories.map((cat) => (
            <Card key={cat.id}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${cat.color || 'from-blue-500 to-cyan-500'} flex items-center justify-center`}>
                    <Briefcase className="w-4 h-4 text-white" />
                  </div>
                  <span>{cat.name}</span>
                  <Badge variant="secondary" className="ml-auto">{(cat.subcategories||[]).length} alt kategori</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {Array.isArray(cat.features) && cat.features.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-gray-700">Neler sunuyoruz</h4>
                    <div className="grid grid-cols-1 gap-2">
                      {cat.features.slice(0,3).map((f, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>{typeof f === 'string' ? f : f.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-700">Alt Kategoriler</h4>
                  <div className="space-y-2">
                    {(cat.subcategories || []).map((sub, idx) => {
                      const subSlug = (sub.name || '')
                        .toLowerCase()
                        .replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's')
                        .replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ç/g, 'c')
                        .replace(/&/g, 've').replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                      return (
                        <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div>
                            <p className="font-medium text-gray-900">{sub.name}</p>
                            {sub.description && <p className="text-xs text-gray-600">{sub.description}</p>}
                          </div>
                          <Link to={`/kategori/${cat.slug || cat.id}/${subSlug}`}>
                            <Button size="sm" variant="outline">
                              Detay <ArrowRight className="w-4 h-4 ml-1" />
                            </Button>
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="pt-2">
                  <Link to={`/kategori/${cat.slug || cat.id}`}>
                    <Button className="w-full" variant="secondary">Kategoriyi Gör</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProjectsCatalogPage;






